import type { Namespace, SupportedLanguage } from "./constants";

type LocaleModule = { default?: Record<string, unknown> } | Record<string, unknown>;

function unwrap(mod: LocaleModule): Record<string, unknown> {
  if (mod && typeof mod === "object" && "default" in mod && mod.default) {
    return mod.default as Record<string, unknown>;
  }
  return mod as Record<string, unknown>;
}

/**
 * Dynamic import map — each JSON becomes its own Vite chunk so Discovery
 * never sync-bundles unused languages or the admin namespace.
 */
const LOADERS: Record<
  SupportedLanguage,
  Record<Namespace, () => Promise<LocaleModule>>
> = {
  de: {
    common: () => import("./locales/de/common.json"),
    consumer: () => import("./locales/de/consumer.json"),
    vendor: () => import("./locales/de/vendor.json"),
    auth: () => import("./locales/de/auth.json"),
    admin: () => import("./locales/de/admin.json"),
  },
  en: {
    common: () => import("./locales/en/common.json"),
    consumer: () => import("./locales/en/consumer.json"),
    vendor: () => import("./locales/en/vendor.json"),
    auth: () => import("./locales/en/auth.json"),
    admin: () => import("./locales/en/admin.json"),
  },
  fr: {
    common: () => import("./locales/fr/common.json"),
    consumer: () => import("./locales/fr/consumer.json"),
    vendor: () => import("./locales/fr/vendor.json"),
    auth: () => import("./locales/fr/auth.json"),
    admin: () => import("./locales/fr/admin.json"),
  },
  it: {
    common: () => import("./locales/it/common.json"),
    consumer: () => import("./locales/it/consumer.json"),
    vendor: () => import("./locales/it/vendor.json"),
    auth: () => import("./locales/it/auth.json"),
    admin: () => import("./locales/it/admin.json"),
  },
};

const inflight = new Map<string, Promise<Record<string, unknown>>>();

/** Load one locale JSON namespace (deduped). */
export function loadLocaleNamespace(
  lng: SupportedLanguage,
  ns: Namespace,
): Promise<Record<string, unknown>> {
  const key = `${lng}:${ns}`;
  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = LOADERS[lng][ns]()
    .then(unwrap)
    .finally(() => {
      /* keep resolved promise cached via inflight for dedupe of in-flight only;
         i18next holds the bundle after addResourceBundle */
    });
  inflight.set(key, promise);
  return promise;
}
